/**
 * WhatsApp-call permission logic.
 *
 * The other party must opt in before we may place a WhatsApp call. That state
 * rides on the Stream channel as `custom_data.call_*` fields (see
 * [services/stream/types.ts]). When it is missing or not `approved`, the agent
 * must first send a permission request (see `sendCallRequest` in
 * [services/calls/client.ts]).
 */
import type { UniverseChannelCustomData } from '@/services/stream/types';

/**
 * Parses `call_expired_at` into an epoch-ms timestamp. Handles both the
 * colon-separated (`'YYYY-MM-DD HH:MM:SS'`) and dash-separated
 * (`'YYYY-MM-DD HH-MM-SS'`) time formats the backend has been seen to send.
 * Returns `null` when absent or unparseable.
 */
export function parseCallExpiry(value?: string): number | null {
    if (!value) return null;
    const [datePart, timePart] = value.trim().split(/\s+/);
    if (!datePart) return null;
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour = 0, minute = 0, second = 0] = (timePart ?? '').split(/[-:]/).map(Number);
    if (!year || !month || !day) return null;
    const ts = new Date(year, month - 1, day, hour, minute, second).getTime();
    return Number.isNaN(ts) ? null : ts;
}

/**
 * Whether a WhatsApp call may be placed for a channel right now.
 *
 * Requires an `approved` status that is either permanent or not yet expired.
 * When approved with an unparseable/absent expiry we err on the side of
 * allowing the call.
 */
export function canCallWhatsApp(data?: UniverseChannelCustomData): boolean {
    if (!data || data.call_status !== 'approved') return false;
    if (data.call_is_permanent) return true;
    const expiry = parseCallExpiry(data.call_expired_at);
    if (expiry == null) return true;
    return expiry > Date.now();
}
